import { Box, Grid, useMediaQuery } from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import React from 'react'

import { Theme } from '../../lib/theming'
import Image from '../shared/Image'
import { Body2 } from '../utils/typography'

const useStyles = makeStyles<Theme>(({ breakpoints, spacing }) => ({
  avatar: {
    aspectRatio: '1/1',
    borderRadius: '50%',
    height: '100%',
    objectFit: 'cover',
    width: '100%',
  },
  avatarContainer: {
    borderRadius: '50%',
    overflow: 'hidden',
  },
  root: {
    [breakpoints.up('md')]: {
      marginBottom: spacing(1),
    },
  },
}))

export type ParticipantProps = {
  avatarUrl?: string | null
  companyName?: string | null
  id: string
  jobTitle?: string | null
  name: string
}

export const jobDescriptionFor = ({
  jobTitle,
  companyName,
}: {
  companyName?: string | null
  jobTitle?: string | null
  joinWith?: string
}) => (
  <>
    {jobTitle} <br /> {companyName}
  </>
)

const Participant = ({ name, companyName, jobTitle, avatarUrl }: ParticipantProps) => {
  const classes = useStyles()
  const { breakpoints } = useTheme<Theme>()
  const mdUp = useMediaQuery(breakpoints.up('md'))
  const jobDescription = jobDescriptionFor({ companyName, jobTitle })

  return (
    <Grid container item alignItems="center" md={4} spacing={1} xs={12}>
      <>
        {avatarUrl && (
          <Grid item className={classes.avatarContainer} md={3} xs={2}>
            <Image
              className={classes.avatar}
              height={80}
              htmlAttributes={{ alt: name }}
              imgixParams={{ crop: 'faces', fit: 'crop' }}
              lazy={{
                placeholder: {
                  blur: '3px',
                  'corner-radius': '100,100,100,100',
                  mask: 'corners',
                  scale: 0.95,
                },
              }}
              src={avatarUrl}
              width={80}
            />
          </Grid>
        )}
        <Grid item md={avatarUrl ? 9 : 12} xs={avatarUrl ? 10 : 12}>
          {mdUp ? (
            <>
              <Body2 color="textPrimary" style={{ fontWeight: 600 }}>
                {name}
              </Body2>
              {jobDescription && (
                <Body2 color="textPrimary">
                  <Box color="text.secondary" component="span">
                    {' '}
                    {jobDescription}
                  </Box>
                </Body2>
              )}
            </>
          ) : (
            <Body2 color="textPrimary">
              {name}
              {jobDescription && (
                <Box color="text.secondary" component="span">
                  {', '}
                  {jobDescription}
                </Box>
              )}
            </Body2>
          )}
        </Grid>
      </>
    </Grid>
  )
}

const ParticipantsList = ({
  limitVisible,
  participants,
}: {
  limitVisible?: number
  participants: Array<ParticipantProps>
}) => {
  const classes = useStyles()

  const visibleParticipants = limitVisible ? participants.slice(0, limitVisible) : participants
  const numHiddenParticipants = limitVisible ? Math.max(0, participants.length - limitVisible) : 0
  const additionalParticipants = numHiddenParticipants > 0 && (
    <Body2 color="textSecondary">
      +{numHiddenParticipants} {numHiddenParticipants === 1 ? 'speaker' : 'speakers'}
    </Body2>
  )

  return (
    <Grid container classes={{ root: classes.root }} spacing={1}>
      {visibleParticipants.map(({ id, name, jobTitle, companyName, avatarUrl }) => (
        <Participant
          key={id}
          avatarUrl={avatarUrl}
          companyName={companyName}
          id={id}
          jobTitle={jobTitle}
          name={name}
        />
      ))}
      {additionalParticipants}
    </Grid>
  )
}

export default ParticipantsList
